import { SelectOrdinal } from "@lingui/react/macro";
import { Fragment } from "react/jsx-runtime";
import { useLingui } from "@lingui/react/macro";
import { ANY_TIME_OF_DAY, type IActivity } from "@/api/Activities";
import { timeOfDayDictionary } from "@medoma/models/activities";
import { addTime } from "@/Utils/TimeUtils";
import { weekdayDictionary } from "@/forms/AddActivity/Scheduling/schedulingModels";
import {
  hoursToHoursAndMinutes,
  timespanToHours,
} from "@/forms/AddActivity/Scheduling/timeSlotsUtils";
import { dateName } from "@/Utils/DateUtils";
import { Text } from "@medoma/components/Text/Text";
import styles from "./ScheduleSummary.module.scss";

const daysToWeeks = (days: number) => days / 7;

const TimeSpan = ({ start, timespan }: { start: string; timespan: string }) => {
  const end = addTime(start, timespan);
  return <time>{`${start}-${end}`}</time>;
};

export const Frequency = ({
  schedule,
}: {
  schedule: IActivity["schedule"];
}) => {
  const { t } = useLingui();

  const hasIntervalRecurrence = "interval" in schedule;
  const hasWeekdaysRecurrence = "days" in schedule;

  if (hasWeekdaysRecurrence) {
    return (
      <>
        {schedule.days
          .map((day) => t(weekdayDictionary[day].regular))
          .join(", ")}
      </>
    );
  }

  if (hasIntervalRecurrence) {
    const numberOfDays = schedule.interval;
    const showIntervalAsWeeks = numberOfDays >= 7 && numberOfDays % 7 === 0;

    if (showIntervalAsWeeks) {
      return (
        <SelectOrdinal
          value={daysToWeeks(numberOfDays)}
          _1="Varje vecka"
          _2="Varannan vecka"
          one="Var #:a vecka"
          two="Var #:a vecka"
          few="Var #:e vecka"
          other="Var #:e vecka"
        />
      );
    }

    return (
      <SelectOrdinal
        value={numberOfDays}
        _1="Varje dag"
        _2="Varannan dag"
        one="Var #:a dag"
        two="Var #:a dag"
        few="Var #:e dag"
        other="Var #:e dag"
      />
    );
  }

  return <>{dateName(new Date(schedule.date))}</>;
};

export const Times = ({ schedule }: { schedule: IActivity["schedule"] }) => {
  const { t } = useLingui();

  if ("times" in schedule) {
    if (schedule.times === ANY_TIME_OF_DAY) {
      return <>{t(timeOfDayDictionary.Any.long)}</>;
    }
    return schedule.times.map((time, i) => {
      const divider = i === schedule.times.length - 1 ? "" : ", ";
      return (
        <Fragment key={time}>
          <TimeSpan start={time} timespan={schedule.span} />
          {divider}
        </Fragment>
      );
    });
  }

  if ("time" in schedule) {
    if (schedule.time === ANY_TIME_OF_DAY) {
      return <>{t(timeOfDayDictionary.Any.long)}</>;
    }
    return <TimeSpan start={schedule.time} timespan={schedule.span} />;
  }

  return null;
};

export const ScheduleSummary = ({
  schedule,
}: {
  schedule: IActivity["schedule"];
}) => {
  const scheduledTime =
    "times" in schedule
      ? schedule.times
      : "time" in schedule
        ? schedule.time
        : null;
  const hasSpecificTimes =
    scheduledTime !== null && scheduledTime !== ANY_TIME_OF_DAY;

  return (
    <div className={styles.summary}>
      <Text element="span" color="faded">
        <Frequency schedule={schedule} />
      </Text>
      <Text element="span" color="faded">
        <Times schedule={schedule} />
      </Text>
      {hasSpecificTimes ? (
        <Text element="span" color="faded">
          {hoursToHoursAndMinutes(timespanToHours(schedule.span))}
        </Text>
      ) : null}
    </div>
  );
};
